import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, Loader2, CheckCircle2, AlertCircle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Education", "Healthcare", "Environment", "Rural Development", "Women Empowerment", "Livelihood", "Other"];
const BUDGETS = ["Under ₹25 Lakhs", "₹25L - ₹1 Crore", "₹1 Cr - ₹5 Cr", "Above ₹5 Cr"];
const TARGETS = ["₹10L - ₹50L", "₹50L - ₹2 Cr", "₹2 Cr+"];
const PLANS = ["CSR Launchpad", "Scale & Growth", "Institutional Enterprise", "Custom Consultation", "Undecided"];
const URGENCIES = ["Immediate", "Within 30 Days", "Exploration"];

const inputClass =
  "w-full rounded-xl border border-[#E6E9E6] bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#0E2A6B] focus:ring-2 focus:ring-[#0E2A6B]/20";

const initialForm = {
  full_name: "",
  ngo_name: "",
  email: "",
  phone: "",
  sector: "",
  budget_range: "",
  target_grant: "",
  message: "",
  urgency: "Within 30 Days",
};

export default function QueryForm({ selectedPlan, onPlanChange, planPulse }) {
  const [form, setForm] = useState(initialForm);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [stats, setStats] = useState(null);
  const selectRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/callbacks/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (planPulse > 0 && selectRef.current) {
      selectRef.current.classList.remove("plan-highlight");
      void selectRef.current.offsetWidth;
      selectRef.current.classList.add("plan-highlight");
    }
  }, [planPulse]);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors({ ...errors, [key]: "" });
  };

  const validate = () => {
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name";
    if (form.ngo_name.trim().length < 2) errs.ngo_name = "Please enter your NGO / Trust name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid official email";
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone.trim())) errs.phone = "Enter a valid 10+ digit phone number";
    if (!form.sector) errs.sector = "Select your sector of work";
    if (!form.budget_range) errs.budget_range = "Select your annual budget range";
    if (!form.target_grant) errs.target_grant = "Select your funding target";
    if (!consent) errs.consent = "Please consent to be contacted";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccess(false);
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/callbacks`, { ...form, preferred_plan: selectedPlan });
      setSuccess(true);
      setForm(initialForm);
      setConsent(false);
      toast.success("Callback request received!", {
        description: "Our CSR Advisory Lead will contact you within 24 business hours.",
      });
      axios.get(`${API}/callbacks/stats`).then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ k }) =>
    errors[k] ? <p className="mt-1 text-xs font-medium text-red-600">{errors[k]}</p> : null;

  return (
    <section id="query-callback" className="bg-[#F4F3EE] py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <div>
          <p className="font-mono-alt mb-4 text-xs font-semibold tracking-[0.35em] text-[#E8862A]">06 / REQUEST A CALLBACK</p>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C7DAF5] bg-[#EAF1FC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E2A6B]">
            <PhoneCall className="h-3.5 w-3.5" />
            Request a Callback
          </span>
          <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Tell us about your NGO —{" "}
            <span className="font-display italic text-[#0E2A6B]">we'll call you back</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Share a few details about your organization and funding goals. Our CSR Advisory Lead
            will review your eligibility and call you within{" "}
            <span className="font-semibold text-slate-900">24 business hours</span> with next steps
            and a customized association proposal.
          </p>

          <ul className="mt-8 space-y-4">
            {[
              "Free 20-minute eligibility & readiness audit",
              "Your details stay confidential; never shared with corporates without consent",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0E2A6B]" />
                {t}
              </li>
            ))}
          </ul>

          {stats && (
            <div className="mt-8 inline-flex items-center gap-2.5 rounded-2xl border border-[#E6E9E6] bg-white px-5 py-3.5">
              <Users className="h-5 w-5 text-[#E8862A]" />
              <p className="text-sm text-slate-700">
                <span className="font-bold text-slate-900">{stats.this_week} NGOs</span> requested a
                consultation this week
              </p>
            </div>
          )}
        </div>

        <form
          data-testid="query-callback-form"
          onSubmit={submit}
          className="rounded-3xl border border-[#E6E9E6] bg-white p-6 shadow-xl sm:p-8"
          noValidate
        >
          {success && (
            <div
              data-testid="query-form-success-alert"
              className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              <p className="text-sm text-emerald-900">
                <span className="font-semibold">Request received.</span> Our CSR Advisory Lead will
                contact you within 24 business hours.
              </p>
            </div>
          )}
          {errorMsg && (
            <div
              data-testid="query-form-error-alert"
              className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5"
            >
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <p className="text-sm text-red-800">{errorMsg}</p>
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name *</label>
              <input data-testid="query-input-full-name" className={inputClass} placeholder="Ananya Sharma" value={form.full_name} onChange={set("full_name")} />
              <Err k="full_name" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">NGO / Trust Name *</label>
              <input data-testid="query-input-ngo-name" className={inputClass} placeholder="Sunrise Welfare Trust" value={form.ngo_name} onChange={set("ngo_name")} />
              <Err k="ngo_name" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Official Email *</label>
              <input data-testid="query-input-email" type="email" className={inputClass} placeholder="you@ngo.org" value={form.email} onChange={set("email")} />
              <Err k="email" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone / WhatsApp *</label>
              <input data-testid="query-input-phone" className={inputClass} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} />
              <Err k="phone" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Sector of Work *</label>
              <select data-testid="query-select-sector" className={inputClass} value={form.sector} onChange={set("sector")}>
                <option value="">Select sector</option>
                {SECTORS.map((s) => <option key={s}>{s}</option>)}
              </select>
              <Err k="sector" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Annual Budget Range *</label>
              <select data-testid="query-select-budget" className={inputClass} value={form.budget_range} onChange={set("budget_range")}>
                <option value="">Select budget</option>
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
              <Err k="budget_range" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Desired CSR Funding Target *</label>
              <select data-testid="query-select-target" className={inputClass} value={form.target_grant} onChange={set("target_grant")}>
                <option value="">Select target</option>
                {TARGETS.map((t) => <option key={t}>{t}</option>)}
              </select>
              <Err k="target_grant" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Preferred Association Plan</label>
              <select
                ref={selectRef}
                data-testid="query-select-association-plan"
                className={`${inputClass} border-2`}
                value={selectedPlan}
                onChange={(e) => onPlanChange(e.target.value)}
              >
                {PLANS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">How soon do you need funding?</label>
              <div className="grid grid-cols-3 gap-2">
                {URGENCIES.map((u) => (
                  <button
                    type="button"
                    key={u}
                    data-testid={`query-urgency-${u.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm({ ...form, urgency: u })}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all sm:text-sm ${
                      form.urgency === u
                        ? "border-[#0E2A6B] bg-[#EAF1FC] text-[#0E2A6B]"
                        : "border-[#E6E9E6] text-slate-600 hover:border-[#3D63B8]/40"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Message / Project Requirements</label>
              <textarea
                data-testid="query-textarea-message"
                rows={4}
                className={inputClass}
                placeholder="Briefly describe your programs, current funders, and what you need help with…"
                value={form.message}
                onChange={set("message")}
              />
            </div>
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-3">
            <input
              data-testid="query-checkbox-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                setErrors({ ...errors, consent: "" });
              }}
              className="mt-1 h-4 w-4 rounded border-[#E6E9E6] accent-[#0E2A6B]"
            />
            <span className="text-xs leading-relaxed text-slate-600">
              I consent to Impact Sutra contacting me via phone, WhatsApp, or email regarding
              CSR fundraising advisory services. My details will not be shared with third parties
              without permission.
            </span>
          </label>
          <Err k="consent" />

          <button
            data-testid="query-form-submit-button"
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0E2A6B] px-7 py-4 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#0A1F50] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <PhoneCall className="h-4 w-4" />
                Request Callback — Free Eligibility Audit
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
